import { fabric } from 'fabric';
import { fxCanvas } from '@frontend/lib/glfx';
import { cloneFabricObject } from '../helpers/clone-handler';

export class SkewEffect {
  isActive(obj) {
    const skew = obj.smartObject?.skew || obj.skew;
    return (
      skew &&
      Array.isArray(skew.value) &&
      skew.value.length === 8 &&
      !skew.value.every(v => v === 0) &&
      skew.render
    );
  }

  apply(obj, effectsRenderBuffer) {
    const ref = obj.smartObject || obj;

    const skew = ref.smartObject?.skew || ref.skew;

    obj.setCoords();

    const elementWidth = ref.getScaledWidth();
    const elementHeight = ref.getScaledHeight();

    const originalBoxCoords = [
      0, 0,
      elementWidth, 0,
      elementWidth, elementHeight,
      0, elementHeight
    ];

    const modifiedCoords = skew.value.map((v, i) => originalBoxCoords[i] + v);

    const xCoords = [modifiedCoords[0], modifiedCoords[2], modifiedCoords[4], modifiedCoords[6]];
    const yCoords = [modifiedCoords[1], modifiedCoords[3], modifiedCoords[5], modifiedCoords[7]];

    const minX = Math.min(0, ...xCoords);
    const maxX = Math.max(elementWidth, ...xCoords);
    const minY = Math.min(0, ...yCoords);
    const maxY = Math.max(elementHeight, ...yCoords);

    const offsetX = minX < 0 ? -minX : 0;
    const offsetY = minY < 0 ? -minY : 0;

    const canvasWidth = Math.ceil(maxX - minX);
    const canvasHeight = Math.ceil(maxY - minY);

    const tempCanvas = new fabric.StaticCanvas(null, {
      width: canvasWidth,
      height: canvasHeight,
      enableRetinaScaling: false,
    });

    const elementData = obj.toObject();
    elementData.skew = null;
    elementData.shadow = null;

    if (obj.smartObject) {
      elementData.left = obj.left - obj.smartObject.left + offsetX;
      elementData.top = obj.top - obj.smartObject.top + offsetY;
    } else {
      elementData.left = offsetX;
      elementData.top = offsetY;
    }

    if (obj.clipPath) {
      const cp = fabric.util.object.clone(obj.clipPath);
      cp.absolutePositioned = true;
      cp.left = (cp.left || 0) - obj.left + elementData.left;
      cp.top = (cp.top || 0) - obj.top + elementData.top;
      elementData.clipPath = cp;
    }

    const clone = effectsRenderBuffer.canvas
      ? new fabric.Image(effectsRenderBuffer.canvas)
      : cloneFabricObject(obj, elementData);

    tempCanvas.add(clone);
    tempCanvas.renderAll();

    const texture = fxCanvas.texture(tempCanvas.getElement());

    const originalCoords = [
      offsetX, offsetY,
      offsetX + elementWidth, offsetY,
      offsetX + elementWidth, offsetY + elementHeight,
      offsetX, offsetY + elementHeight
    ];

    const adjustedModifiedCoords = [
      modifiedCoords[0] + offsetX, modifiedCoords[1] + offsetY,
      modifiedCoords[2] + offsetX, modifiedCoords[3] + offsetY,
      modifiedCoords[4] + offsetX, modifiedCoords[5] + offsetY,
      modifiedCoords[6] + offsetX, modifiedCoords[7] + offsetY,
    ];

    obj.wCoords = {
      tl: { x: modifiedCoords[0] + obj.left, y: modifiedCoords[1] + obj.top },
      tr: { x: modifiedCoords[2] + obj.left, y: modifiedCoords[3] + obj.top },
      br: { x: modifiedCoords[4] + obj.left, y: modifiedCoords[5] + obj.top },
      bl: { x: modifiedCoords[6] + obj.left, y: modifiedCoords[7] + obj.top }
    };

    fxCanvas
      .draw(texture)
      .perspective(originalCoords, adjustedModifiedCoords)
      .update();

    texture.destroy();

    const resultCanvas = document.createElement('canvas');
    const resultCanvasCtx = resultCanvas.getContext('2d');

    resultCanvas.width = canvasWidth;
    resultCanvas.height = canvasHeight;

    resultCanvasCtx.drawImage(fxCanvas, 0, 0);

    return {
      canvas: resultCanvas,
      offsetX: offsetX - (ref.left - obj.left),
      offsetY: offsetY - (ref.top - obj.top)
    }
  }
}
